import { Injectable, signal } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { environment } from '@envs/environment';

@Injectable({
    providedIn: 'root'
})
export class TokensService {
    readonly #accessId: string = environment.session_token_key;

    readonly #refreshId: string = environment.session_refresh_key;

    public accessExpires: number = 300;

    public refreshExpires: number = 1000;

    public validToken = signal(false);

    public get accessToken(): string {
        return sessionStorage.getItem(this.#accessId) || '';
    }

    public set accessToken(token: string) {
        sessionStorage.setItem(this.#accessId, token);
    }

    public get hasAccessTokenStored(): boolean {
        this.validToken.set(!!this.accessToken);
        return !!this.accessToken;
    }

    /**
     * Whether the stored token belongs to a Clicklease admin rather than a ROSE.
     *
     * Read straight from the token because it has to be answerable before the store exists —
     * on a cold page load the `isAdmin` flag has not been restored yet, and which of the two
     * flows the app is in decides where an unauthenticated user is sent back to.
     *
     * A token that cannot be decoded is treated as not-admin: the ROSE login is the safe
     * destination of the two, since an admin who lands there can still reach `/admin/login`.
     */
    public get isAdminToken(): boolean {
        const token = this.accessToken;
        if (!token) {
            return false;
        }
        try {
            const { user_roles } = jwtDecode<{ user_roles?: string[] }>(token);
            return (user_roles || []).includes('Clicklease Admin');
        } catch {
            return false;
        }
    }

    public get refreshToken(): string {
        return sessionStorage.getItem(this.#refreshId) || '';
    }

    public set refreshToken(token: string) {
        sessionStorage.setItem(this.#refreshId, token);
    }

    public clearTokens(): void {
        sessionStorage.removeItem(this.#accessId);
        sessionStorage.removeItem(this.#refreshId);
        this.validToken.set(false);
    }

    public setExpiressIn(refresh: number, access: number): void {
        this.refreshExpires = refresh;
        this.accessExpires = access;
    }

    public setTokens(access: string, refresh: string): void {
        this.accessToken = access;
        this.refreshToken = refresh;
        this.validToken.set(true);
    }
}